import cn from 'classnames';
import { useCallback, useState } from 'react';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { Hamburger } from '#components/atoms/buttons/hamburger/Hamburger.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { Logo } from '#components/atoms/logo/Logo.tsx';
import { NavGroupItem } from '#components/atoms/nav/nav-group-item/NavGroupItem.tsx';
import { NavGroup } from '#components/atoms/nav/nav-group/NavGroup.tsx';
import { NavItem } from '#components/atoms/nav/nav-item/NavItem.tsx';
import { Nav } from '#components/atoms/nav/Nav.tsx';
import { SkipLink } from '#components/atoms/skip-link/SkipLink.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useWhatsNew } from '#global/context/whats-new/useWhatsNew.tsx';
import { WhatsNewProvider } from '#global/context/whats-new/WhatsNewProvider.tsx';
import { usePersistedNetworkFilter } from '#hooks/usePersistedNetworkFilter.tsx';
import { UserMenu } from '../../sidebar/user-menu/UserMenu.tsx';
import { UpdatesPanel } from '../../updates/updates-panel/UpdatesPanel.tsx';
import styles from './NavLayout.module.css';

type Props = {
  children: React.ReactNode;
};

export function NavLayout({ children }: Props) {
  return (
    <WhatsNewProvider>
      <NavLayoutInner>{children}</NavLayoutInner>
    </WhatsNewProvider>
  );
}

function NavLayoutInner({ children }: Props) {
  const [isMenuOpen, setMenuOpen] = useState(false);
  // We only want one of settings or support to be open at a time,
  // and it could be different from the active one, so track separately
  // IMPORTANT: If adding more NavGroups, update these types, as well as the `setActiveSubnav(null)` guards
  const [openSubnav, setOpenSubnav] = useState<'settings' | 'support' | null>(null);
  const [activeSubnav, setActiveSubnav] = useState<'settings' | 'support' | null>(null);
  const persistedNetworkFilter = usePersistedNetworkFilter();
  const { user } = useAuth();
  const { isOpen: isPanelOpen, close: closePanel, updates } = useWhatsNew();

  // Get feature flags
  const { data: featureFlags } = useSuspenseFeatureFlagsQuery();

  const { LIST_CERT_AUTHORITIES, ADMIN_USER_MANAGEMENT_LIMIT, MANAGED_DNS } = featureFlags;

  function closeMenu() {
    setMenuOpen(false);
  }

  const setIsOpenSettings = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setOpenSubnav('settings');
      } else if (openSubnav === 'settings') {
        // Only unset the open nav if we are open
        setOpenSubnav(null);
      }
    },
    [openSubnav]
  );
  const setIsOpenSupport = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setOpenSubnav('support');
      } else if (openSubnav === 'support') {
        // Only unset the open nav if we are open
        setOpenSubnav(null);
      }
    },
    [openSubnav]
  );

  const setIsActiveSettings = useCallback(
    (isActive: boolean) => {
      if (isActive) {
        setActiveSubnav('settings');
      } else if (activeSubnav === 'settings') {
        setActiveSubnav(null);
      }
    },
    [activeSubnav]
  );
  const setIsActiveSupport = useCallback(
    (isActive: boolean) => {
      if (isActive) {
        setActiveSubnav('support');
      } else if (activeSubnav === 'support') {
        setActiveSubnav(null);
      }
    },
    [activeSubnav]
  );

  return (
    <div className={cn(styles.NavLayout, { [styles.NavLayout___sidebarVisible]: isMenuOpen })}>
      <div className={styles.NavLayout_header}>
        <SkipLink href="#page-main">Go to main content</SkipLink>
        <Link to="/">
          <Logo height={32} environment={__SENTRY_ENVIRONMENT__} />
        </Link>
        <div className={styles.NavLayout_hamburger}>
          <Hamburger
            isOpen={isMenuOpen}
            onClick={() => {
              setMenuOpen(!isMenuOpen);
            }}
          />
        </div>
      </div>
      <div className={styles.NavLayout_sidebar}>
        <Nav>
          <NavItem to={{ pathname: '/hosts', search: persistedNetworkFilter }} onClick={closeMenu}>
            Hosts
          </NavItem>
          <NavItem to="/users" onClick={closeMenu}>
            Users
          </NavItem>
          <NavItem to="/routes" onClick={closeMenu}>
            Routes
          </NavItem>
          <NavItem to="/roles" onClick={closeMenu}>
            Roles
          </NavItem>

          {LIST_CERT_AUTHORITIES ?
            <NavItem to="/certificate-authorities" onClick={closeMenu}>
              Certificate Authorities
            </NavItem>
          : null}

          <NavItem to="/tags" onClick={closeMenu}>
            Tags
          </NavItem>
          <NavItem to="/logs/audit" onClick={closeMenu}>
            Logs
          </NavItem>

          {MANAGED_DNS ?
            <NavItem to="/dns-preview" onClick={closeMenu}>
              DNS preview
            </NavItem>
          : null}
          <NavGroup
            title="Settings"
            isOpen={openSubnav === 'settings'}
            isActive={activeSubnav === 'settings'}
            setOpen={setIsOpenSettings}
            setActive={setIsActiveSettings}
          >
            <NavGroupItem to="/settings/billing" onClick={closeMenu}>
              Billing
            </NavGroupItem>
            <NavGroupItem to="/settings/networks" onClick={closeMenu}>
              Networks
            </NavGroupItem>
            <NavGroupItem to="/settings/api-keys" onClick={closeMenu}>
              API keys
            </NavGroupItem>
            <NavGroupItem to="/settings/join-keys" onClick={closeMenu}>
              Host join keys
            </NavGroupItem>
            <NavGroupItem to="/settings/sso" onClick={closeMenu}>
              Single sign-on
            </NavGroupItem>
            {ADMIN_USER_MANAGEMENT_LIMIT && user?.type === 'user' ?
              <NavGroupItem to="/settings/admins" onClick={closeMenu}>
                Admins
              </NavGroupItem>
            : null}
          </NavGroup>
          <NavGroup
            title="Support"
            isOpen={openSubnav === 'support'}
            isActive={activeSubnav === 'support'}
            setOpen={setIsOpenSupport}
            setActive={setIsActiveSupport}
          >
            <NavGroupItem to="https://www.defined.net/docs/" external={true}>
              Documentation
            </NavGroupItem>
            <NavGroupItem to="/support/contact" onClick={closeMenu}>
              Contact us
            </NavGroupItem>
            <NavGroupItem to="/downloads" onClick={closeMenu}>
              Downloads
            </NavGroupItem>
          </NavGroup>
        </Nav>
        <div className={styles.NavLayout_sidebarFooter}>
          <UserMenu />
        </div>
      </div>
      <main id="page-main" className={styles.NavLayout_main}>
        {children}
      </main>
      {/* Rooted here, not in the menu that opens it, so it outlives the menu closing */}
      <UpdatesPanel isOpen={isPanelOpen} onClose={closePanel} updates={updates} />
    </div>
  );
}
